import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  FormControl,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  type SelectChangeEvent,
} from "@mui/material";
import { usePrefectures } from "@/hooks/usePrefectures";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  type TradePriceListFilter,
  type PriceClassificationType,
} from "@/features/map/types/tradePrice";

interface IProps {
  open: boolean;
  onClose: () => void;
  initialFilters: TradePriceListFilter;
  onApply: (filters: TradePriceListFilter) => void;
}

/**
 * 取引時期の選択肢を生成する関数
 * @returns 年の選択肢（現在年から過去4年分）
 */
const generateYearOptions = (): number[] => {
  const currentYear = new Date().getFullYear();
  return [
    currentYear,
    currentYear - 1,
    currentYear - 2,
    currentYear - 3,
    currentYear - 4,
  ];
};

/**
 * 取引時期の初期値を取得する関数
 * @returns 前年（現在年 - 1）
 */
export const getDefaultTradePriceYear = (): number => {
  return new Date().getFullYear() - 1;
};

export const TradePriceFilterModal: React.FC<IProps> = ({
  open,
  onClose,
  initialFilters,
  onApply,
}) => {
  const isMobile = useIsMobile();
  const { prefectures } = usePrefectures();
  const yearOptions = generateYearOptions();

  const [filters, setFilters] = useState<TradePriceListFilter>(initialFilters);

  useEffect(() => {
    if (open) {
      setFilters(initialFilters);
    }
  }, [initialFilters, open]);

  const handleApply = (): void => {
    onApply(filters);
    onClose();
  };

  const handleClose = (
    _: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    // 入力中の誤タップ・Esc で閉じない。閉じるのは「閉じる」・検索ボタンだけ
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onClose();
  };

  const handlePrefectureChange = (event: SelectChangeEvent<number>): void => {
    setFilters({
      ...filters,
      prefecture: event.target.value as number,
    });
  };

  const handlePriceClassificationChange = (
    type: PriceClassificationType,
    checked: boolean
  ): void => {
    const newClassifications = checked
      ? [...filters.priceClassifications, type]
      : filters.priceClassifications.filter((t) => t !== type);
    setFilters({
      ...filters,
      priceClassifications: newClassifications,
    });
  };

  const handleYearChange = (event: SelectChangeEvent<number>): void => {
    setFilters({
      ...filters,
      year: event.target.value as number,
    });
  };

  const handleLocationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setFilters({
      ...filters,
      location: event.target.value,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6">検索条件</Typography>
          <Button variant="outlined" onClick={onClose}>
            × 閉じる
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {/* 都道府県 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              都道府県{" "}
              <Typography component="span" color="error">
                *
              </Typography>
            </Typography>
            <FormControl fullWidth required>
              <Select
                value={filters.prefecture ?? ""}
                onChange={handlePrefectureChange}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  都道府県を選択
                </MenuItem>
                {prefectures.map((prefecture) => (
                  <MenuItem
                    key={prefecture.prefCode}
                    value={Number(prefecture.prefCode)}
                  >
                    {prefecture.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* 住所（都道府県を除く） */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              住所（都道府県を除く）
            </Typography>
            <FormControl fullWidth>
              <TextField
                value={filters.location ?? ""}
                onChange={handleLocationChange}
                placeholder="例：千代田区"
                fullWidth
              />
            </FormControl>
          </Box>

          {/* 価格情報区分 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              価格情報区分
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.priceClassifications.includes(
                      "realEstateTransaction"
                    )}
                    onChange={(e) => {
                      handlePriceClassificationChange(
                        "realEstateTransaction",
                        e.target.checked
                      );
                    }}
                  />
                }
                label="不動産取引価格情報"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.priceClassifications.includes(
                      "contractPrice"
                    )}
                    onChange={(e) => {
                      handlePriceClassificationChange(
                        "contractPrice",
                        e.target.checked
                      );
                    }}
                  />
                }
                label="成約価格情報"
              />
            </FormGroup>
          </Box>

          {/* 取引時期 */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              取引時期
            </Typography>
            <FormControl fullWidth>
              <Select value={filters.year} onChange={handleYearChange}>
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}年
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center" }}>
        <Button
          onClick={handleApply}
          color="primary"
          variant="contained"
          size="large"
          sx={{ minWidth: 120 }}
          disabled={
            filters.prefecture === null ||
            filters.priceClassifications.length === 0
          }
        >
          検索
        </Button>
      </DialogActions>
    </Dialog>
  );
};
